import { Auth, type ProcessEventResponse } from "@saasbase-io/loginflow-websdk";
import { html, LitElement, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";

import { NAMESPACE } from "@/constants";
import type { SbAuthProvider } from "@/types";
import { genRandSeed } from "@/utils";

import {
  PROVIDER_BUTTON_DEFAULT_PROPS as DEFAULT_PROPS,
  PROVIDER_ICON_FRAGMENTS,
  PROVIDER_NAMES,
} from "./button.defs";
import type {
  SbProviderButtonProps as Props,
  ProviderButtonContent,
  ProviderButtonIconAlign,
  ProviderButtonPart,
  ProviderButtonSize,
  ProviderButtonVariant,
} from "./button.types";

/**
 * Button that triggers an authentication flow with a specific auth provider.
 *
 * @part provider-button - rtg-button: root
 * @part provider-button-icon - svg/rtg-spinner: icon or loading indicator
 *
 * @event sb-provider-button:click - Fired when the button is clicked, before the auth call.
 *  Detail: `{ id: string; provider: SbAuthProvider; event?: string }`
 * @event sb-provider-button:success - Fired after the auth call resolves successfully.
 *  Detail: `{ id: string; provider: SbAuthProvider; event: string; result: ProcessEventResponse }`
 * @event sb-provider-button:error - Fired when the auth call fails.
 *  Detail: `{ id: string; provider: SbAuthProvider; event: string; message: string }`
 */
@customElement("sb-provider-button")
export class SbProviderButton extends LitElement implements Props {
  static readonly ROOT = "provider-button";
  static readonly ICON = "icon";

  static readonly TAG = `${NAMESPACE}-${SbProviderButton.ROOT}`;

  static readonly CLICK_EVENT = `${SbProviderButton.TAG}:click`;
  static readonly SUCCESS_EVENT = `${SbProviderButton.TAG}:success`;
  static readonly ERROR_EVENT = `${SbProviderButton.TAG}:error`;

  static readonly PARTS: Record<ProviderButtonPart, string> = {
    root: SbProviderButton.ROOT,
    icon: `${SbProviderButton.ROOT}-${SbProviderButton.ICON}`,
  };

  /* ------------------------------------------------------------ */

  /**
   * Substring used in generated part IDs.
   */
  @property({ type: String })
  seed = genRandSeed();

  /**
   * Custom ID used as the root part ID and as the base of subpart IDs.
   */
  @property({ type: String, attribute: "custom-id" })
  customId?: string;

  /**
   * Authentication provider targetted by the button.
   */
  @property({ type: String })
  provider!: SbAuthProvider;

  /**
   * Visual variant of the button component.
   */
  @property({ type: String })
  variant: ProviderButtonVariant = DEFAULT_PROPS.variant;

  /**
   * Size of the button component.
   */
  @property({ type: String })
  size: ProviderButtonSize = DEFAULT_PROPS.size;

  /**
   * Controls what the button renders: icon only, label only, or both.
   */
  @property({ type: String })
  content: ProviderButtonContent = DEFAULT_PROPS.content;

  /**
   * Side the icon appears on relative to the label.
   */
  @property({ type: String, attribute: "icon-align" })
  iconAlign: ProviderButtonIconAlign = DEFAULT_PROPS.iconAlign;

  /**
   * Text prepended to the provider name in the default label.
   * Ignored when `label` is set explicitly.
   */
  private _prompt = DEFAULT_PROPS.prompt;

  @property({ type: String })
  get prompt() {
    return this._prompt;
  }

  set prompt(value: string) {
    const old = this._prompt;
    this._prompt = value.length ? value : DEFAULT_PROPS.prompt;
    this.requestUpdate("prompt", old);
  }

  /**
   * Custom label text that replaces default `prompt + provider name` string.
   */
  @property({ type: String })
  label?: string;

  /**
   * Event identifier forwarded to the authentication service on click.
   * When omitted, only the custom click event is dispatched.
   */
  @property({ type: String })
  event?: string;

  /**
   * Disables the button, preventing interaction.
   */
  @property({ type: Boolean })
  disabled?: boolean;

  /**
   * Manual alternative to the private `_loading` state.
   */
  @property({ type: Boolean })
  loading?: boolean;

  /* ---------------------------------------- */

  /**
   * Indicates whether the component is dispatching or emitting an event.
   */
  @state()
  private _loading = false;

  /* ------------------------------------------------------------ */

  protected createRenderRoot() {
    return this;
  }

  /* ------------------------------------------------------------ */

  get rootId() {
    return this.customId ?? `${SbProviderButton.TAG}-${this.seed}`;
  }

  get iconId() {
    return `${this.rootId}-${SbProviderButton.ICON}`;
  }

  get isLoading() {
    return this.loading || this._loading;
  }

  get isDisabled() {
    return this.disabled || this.isLoading;
  }

  /* ------------------------------------------------------------ */

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener("click", this._handleClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener("click", this._handleClick);
  }

  /* ------------------------------------------------------------ */

  private _handleClick = async () => {
    if (this.isDisabled) return;
    this._loading = true;

    this.dispatchEvent(
      new CustomEvent(SbProviderButton.CLICK_EVENT, {
        detail: { id: this.rootId, provider: this.provider, event: this.event },
        bubbles: true,
        composed: true,
      }),
    );

    try {
      if (this.event) {
        const result: ProcessEventResponse = await Auth.processLoginflowEvent({
          event: this.event,
        });
        this.dispatchEvent(
          new CustomEvent(SbProviderButton.SUCCESS_EVENT, {
            detail: {
              id: this.rootId,
              provider: this.provider,
              event: this.event,
              result,
            },
            bubbles: true,
            composed: true,
          }),
        );
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      this.dispatchEvent(
        new CustomEvent(SbProviderButton.ERROR_EVENT, {
          detail: {
            id: this.rootId,
            provider: this.provider,
            event: this.event,
            message,
          },
          bubbles: true,
          composed: true,
        }),
      );
    } finally {
      this._loading = false;
    }
  };

  /* ------------------------------------------------------------ */

  private _renderIcon() {
    const cn =
      this.content === "icon" ? "" : `rtg-icon-align-${this.iconAlign}`;

    if (this.isLoading)
      return html`
        <rtg-spinner
          id="${this.iconId}"
          part="${SbProviderButton.PARTS.icon}"
          class="${cn ?? nothing}"
        ></rtg-spinner>
      `;

    if (!this.content.includes("icon")) return nothing;

    return html`
      <svg
        id="${this.iconId}"
        part="${SbProviderButton.PARTS.icon}"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="currentColor"
        class="simple-icons simple-icons-${this.provider} ${cn}"
      >
        ${PROVIDER_ICON_FRAGMENTS[this.provider]}
      </svg>
    `;
  }

  /* ---------------------------------------- */

  render() {
    const name = PROVIDER_NAMES[this.provider];
    const label = this.label ?? html`${this.prompt} ${name}`;

    return html`
      <rtg-button
        id="${this.rootId}"
        part="${SbProviderButton.PARTS.root}"
        data-provider="${this.provider}"
        data-event="${this.event ?? nothing}"
        data-loading="${this.isLoading ? "true" : nothing}"
        variant="${this.variant}"
        size="${this.size}"
        ?disabled="${this.isDisabled}"
      >
        ${this.iconAlign === "start" ? this._renderIcon() : nothing}
        ${this.content.includes("label") ? label : nothing}
        ${this.iconAlign === "end" ? this._renderIcon() : nothing}
      </rtg-button>
    `;
  }
}
